import {
    useCallback,
    useId,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent,
    type ReactElement,
} from "react";
import { AnimatePresence } from "framer-motion";
import { space } from "@tokens/index";
import { ContentSwap } from "@primitives/ContentSwap";
import { Text } from "@primitives/Text";
import { Menu, MenuItem } from "@primitives/overlay";
import { useViewport } from "@primitives/viewport";
import type { QueueSummary, QueueWorkItemTally } from "../../experience-shell/queues";
import { ListEmptyLine, ListError, ListSkeleton } from "./queue-surfaces/ListStates";
import { listStack } from "./queue-surfaces/listSurface";
import { QueueListRow } from "./queue-surfaces/QueueListRow";
import {
    availableQueueRowActions,
    type QueueRowActionContext,
} from "./queue-surfaces/queueRowActions";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface QueueListProps {
    readonly items: readonly QueueSummary[];
    readonly isLoading: boolean;
    readonly error?: Error | null;
    /**
     * Invoked when the user activates a row (click, Enter, Space). When
     * provided, rows behave as buttons. When omitted, rows are inert list
     * items.
     */
    readonly onSelect?: (queue: QueueSummary) => void;
    /**
     * Id of the active queue. The matching row keeps a persistent selected
     * treatment so the rail reflects the workspace filter when focus moves.
     */
    readonly selectedQueueId?: string | null;
    /**
     * Invoked when the user opens "Queue maintenance" from a row menu. When
     * provided, each row exposes an actions kebab.
     */
    readonly onManage?: (queue: QueueSummary) => void;
    /** Ids of queues the signed-in user has muted. Drives the Mute/Unmute item. */
    readonly mutedQueueIds?: ReadonlySet<string>;
    /** Invoked when the user toggles a per-queue mute from a row menu. */
    readonly onToggleMute?: (queue: QueueSummary) => void;
    /**
     * Per-queue Open/Pending tallies indexed by queue id. The row shows the
     * Open count. Missing entries render as an em dash so the row does not
     * jump while the tally request is in flight.
     */
    readonly tallies?: ReadonlyMap<string, QueueWorkItemTally>;
    /**
     * When true, a missing tally reads as "loading" rather than
     * "unavailable". Distinct from `isLoading`, which gates the list.
     */
    readonly talliesLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Style sheet
// ---------------------------------------------------------------------------

const styles = {
    root: {
        width: "100%",
        display: "flex",
        flexDirection: "column" as const,
        gap: space[2],
        boxSizing: "border-box" as const,
    },
    // A compact section title: the name and the total open count on one
    // 32px line, aligned with the row inset. Rank by weight and tone, not
    // size, and reserve the count's width so it never nudges the title.
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: space[3],
        minHeight: 32,
        paddingInline: space[3],
        minWidth: 0,
    },
    total: {
        flexShrink: 0,
        minWidth: "2ch",
        textAlign: "right" as const,
        transition: "opacity 120ms ease-out",
    },
} as const;

/** Sum of the open tallies for the listed queues, or `undefined` until every one is in. */
function totalOpen(
    items: readonly QueueSummary[],
    tallies: ReadonlyMap<string, QueueWorkItemTally> | undefined,
): number | undefined {
    if (tallies === undefined || items.length === 0) return undefined;
    let sum = 0;
    for (const queue of items) {
        const tally = tallies.get(queue.id);
        if (tally === undefined) return undefined;
        sum += tally.open;
    }
    return sum;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function QueueList(props: QueueListProps): ReactElement {
    const {
        items,
        isLoading,
        error,
        onSelect,
        selectedQueueId,
        onManage,
        mutedQueueIds,
        onToggleMute,
        tallies,
        talliesLoading = false,
    } = props;
    const baseId = useId();
    const headingId = `${baseId}-heading`;
    const { coarsePointer } = useViewport();

    const actionContext = useMemo<QueueRowActionContext>(
        () => ({
            ...(onManage !== undefined ? { onManage } : {}),
            ...(mutedQueueIds !== undefined ? { mutedQueueIds } : {}),
            ...(onToggleMute !== undefined ? { onToggleMute } : {}),
        }),
        [onManage, mutedQueueIds, onToggleMute],
    );
    const actions = useMemo(() => availableQueueRowActions(actionContext), [actionContext]);

    // Roving focus: exactly one row is in the Tab sequence. Arrow keys move
    // focus directly; `focusIndex` records where it landed.
    const [focusIndex, setFocusIndex] = useState(-1);
    const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
    const openTotal = totalOpen(items, tallies);

    const focusRow = useCallback((index: number) => {
        setFocusIndex(index);
        rowRefs.current[index]?.focus();
    }, []);

    // One menu for the whole list, anchored to the kebab of the open row.
    const [openMenuQueueId, setOpenMenuQueueId] = useState<string | null>(null);
    const menuAnchorRef = useRef<HTMLElement | null>(null);

    const openMenu = useCallback((queueId: string, anchor: HTMLButtonElement) => {
        menuAnchorRef.current = anchor;
        setOpenMenuQueueId(queueId);
    }, []);

    const closeMenu = useCallback(() => {
        setOpenMenuQueueId(null);
    }, []);

    const handleListKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (items.length === 0) return;
            const current = focusIndex < 0 ? 0 : focusIndex;
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    focusRow(current < items.length - 1 ? current + 1 : 0);
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    focusRow(current > 0 ? current - 1 : items.length - 1);
                    break;
                case "Home":
                    e.preventDefault();
                    focusRow(0);
                    break;
                case "End":
                    e.preventDefault();
                    focusRow(items.length - 1);
                    break;
            }
        },
        [items.length, focusIndex, focusRow],
    );

    const openMenuQueue =
        openMenuQueueId !== null
            ? (items.find((queue) => queue.id === openMenuQueueId) ?? null)
            : null;
    // Keep the last open queue so menu items stay mounted through the close
    // animation. Clearing the id in the same render as `open={false}` would
    // otherwise leave an empty panel fading out.
    const menuQueueRef = useRef<QueueSummary | null>(null);
    if (openMenuQueue !== null) {
        menuQueueRef.current = openMenuQueue;
    }
    const menuQueue = openMenuQueue ?? menuQueueRef.current;
    const menuLabelId =
        menuQueue !== null ? `${baseId}-kebab-${menuQueue.id}` : undefined;

    return (
        <section style={styles.root} aria-labelledby={headingId}>
            <header style={styles.header}>
                <Text as="h2" role="body" weight="semibold" id={headingId} truncate>
                    My queues
                </Text>
                {tallies !== undefined && (
                    <span
                        role="img"
                        aria-label={
                            openTotal !== undefined
                                ? `${String(openTotal)} open work item${openTotal === 1 ? "" : "s"} in total`
                                : "Total open work items loading"
                        }
                        data-queue-total-open={openTotal ?? "pending"}
                        style={{ ...styles.total, opacity: openTotal === undefined ? 0 : 1 }}
                    >
                        <Text role="caption" tone="subtle" tabular>
                            {openTotal ?? "0"}
                        </Text>
                    </span>
                )}
            </header>

            {error !== null && error !== undefined ? (
                <ListError>Couldn&rsquo;t load queues. {error.message}</ListError>
            ) : (
                <ContentSwap
                    ready={!isLoading || items.length > 0}
                    skeleton={
                        <ListSkeleton label="Loading queues" count={3} leading={8} trailing={24} />
                    }
                >
                    {items.length === 0 ? (
                        <ListEmptyLine>No queues yet.</ListEmptyLine>
                    ) : (
                        <div
                            role="list"
                            aria-labelledby={headingId}
                            style={listStack}
                            onKeyDown={handleListKeyDown}
                        >
                            <AnimatePresence initial={false}>
                                {items.map((queue, index) => {
                                    const selected =
                                        selectedQueueId !== undefined &&
                                        selectedQueueId !== null &&
                                        selectedQueueId === queue.id;
                                    const tabbable =
                                        focusIndex === -1 ? index === 0 : focusIndex === index;
                                    return (
                                        <QueueListRow
                                            key={queue.id}
                                            queue={queue}
                                            index={index}
                                            selected={selected}
                                            tabbable={tabbable}
                                            {...(onSelect !== undefined ? { onSelect } : {})}
                                            onFocusRow={setFocusIndex}
                                            rowRef={(element) => {
                                                rowRefs.current[index] = element;
                                            }}
                                            openCount={tallies?.get(queue.id)?.open}
                                            countLoading={talliesLoading}
                                            hasActions={actions.length > 0}
                                            actionsAlwaysVisible={coarsePointer}
                                            kebabId={`${baseId}-kebab-${queue.id}`}
                                            menuOpen={openMenuQueueId === queue.id}
                                            onOpenMenu={openMenu}
                                            onCloseMenu={closeMenu}
                                        />
                                    );
                                })}
                            </AnimatePresence>
                        </div>
                    )}
                </ContentSwap>
            )}

            {actions.length > 0 && (
                <Menu
                    open={openMenuQueue !== null}
                    onClose={closeMenu}
                    anchorRef={menuAnchorRef}
                    placement="bottom-end"
                    {...(menuLabelId !== undefined ? { labelledBy: menuLabelId } : {})}
                >
                    {menuQueue !== null &&
                        actions.map((action) => {
                            const Icon = action.icon(actionContext, menuQueue);
                            return (
                                <MenuItem
                                    key={action.id}
                                    label={action.label(actionContext, menuQueue)}
                                    icon={<Icon size={16} strokeWidth={1.75} aria-hidden="true" />}
                                    onSelect={() => action.invoke(actionContext, menuQueue)}
                                />
                            );
                        })}
                </Menu>
            )}
        </section>
    );
}
